import React, { useEffect, useState } from "react";
import { useAuth } from "@/AuthContext";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MetierPicker } from "@/components/metiers/MetierPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, Building2, Image as ImageIcon, UserPlus, CheckCircle2, SkipForward, Sparkles } from "lucide-react";

/**
 * OnboardingWizard — modal bloquant au 1er login chef.
 * Se déclenche si:
 *  - entreprise.secteur non défini (dépend de la Task 2 pour le fixer)
 *  - onboarding_done !== true
 * S'ouvre APRÈS le KBIS (KbisRequiredModal a la priorité).
 * 3 étapes: secteur → logo → premier salarié (skippable dernière)
 */
export default function OnboardingWizard() {
  const { user, refreshUser } = useAuth();
  const kbisRequired = user?.entreprise?.kbis_required && !user?.entreprise?.kbis_storage_path;
  const hasSecteur = Boolean(user?.entreprise?.secteur);
  const done = Boolean(user?.entreprise?.onboarding_done);
  // Once wizard is opened (initial condition), keep it open until user explicitly finishes
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [metier, setMetier] = useState(null);
  const [metierObj, setMetierObj] = useState(null);
  const [secondaires, setSecondaires] = useState([]);
  const secteur = metier;
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sal, setSal] = useState({ nom: "", prenom: "", email: "", telephone: "", poste: "", contrat: "CDI", taux_horaire: 12.0, password: "" });

  // Only trigger open based on initial conditions ; then stay open until finishOnboarding
  useEffect(() => {
    if (!wizardOpen && !kbisRequired && !done && !hasSecteur) {
      setWizardOpen(true);
      setStep(1);
      setMetier(null);
      setSecondaires([]);
    }
  }, [kbisRequired, done, hasSecteur, wizardOpen]);

  if (!wizardOpen) return null;

  const submitStep1 = async () => {
    if (!metier) { toast.error("Choisissez votre métier principal"); return; }
    setSubmitting(true);
    try {
      await api.post("/chef/entreprise/metiers", { metier_code: metier, metiers_secondaires: secondaires });
      await refreshUser();
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const onPickLogo = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 Mo"); return; }
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const submitStep2 = async (skip = false) => {
    if (skip || !logoFile) {
      setStep(3);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      await api.post("/chef/entreprise/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo enregistré");
      await refreshUser();
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const finishOnboarding = async () => {
    setSubmitting(true);
    try {
      await api.post("/chef/entreprise/onboarding-done");
      toast.success("Bienvenue sur PAG !");
      await refreshUser();
      setWizardOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const submitStep3 = async (skip = false) => {
    if (skip) {
      await finishOnboarding();
      return;
    }
    // Validate salarie form
    if (!sal.nom || !sal.prenom || !sal.telephone || !sal.password || sal.password.length < 6) {
      toast.error("Nom, prénom, téléphone et mot de passe (≥6) obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/chef/salaries", {
        ...sal,
        conges_payes_balance: 25,
        taux_horaire: parseFloat(sal.taux_horaire) || 12.0,
      });
      toast.success(`Salarié ${sal.prenom} ${sal.nom} créé`);
      await finishOnboarding();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-2xl [&>button.absolute]:hidden" data-testid="onboarding-wizard" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black tracking-tighter flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-[#FFD700]" /> Bienvenue sur PAG
          </DialogTitle>
          <DialogDescription className="text-sm">
            En 3 étapes rapides, configurons votre espace. Vous pourrez tout modifier plus tard.
          </DialogDescription>
        </DialogHeader>

        {/* Progress stepper */}
        <div className="flex items-center justify-between border-y border-[#DEE2E6] py-3 my-2" data-testid="wizard-progress">
          {[
            { n: 1, label: "Secteur", icon: Building2 },
            { n: 2, label: "Logo", icon: ImageIcon },
            { n: 3, label: "Salarié", icon: UserPlus },
          ].map((s) => (
            <div key={s.n} className={`flex items-center gap-2 ${step === s.n ? "text-[#0A0A0A]" : step > s.n ? "text-[#059669]" : "text-[#868E96]"}`}>
              <div className={`h-8 w-8 rounded-full border-2 flex items-center justify-center font-bold text-xs ${step === s.n ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : step > s.n ? "border-[#059669] bg-[#059669] text-white" : "border-[#DEE2E6]"}`}>
                {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : s.n}
              </div>
              <span className="text-xs uppercase tracking-wider font-bold hidden sm:block">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Step 1: Métier */}
        {step === 1 && (
          <div className="space-y-4 py-2" data-testid="wizard-step-1">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Votre métier principal <span className="text-[#E60000]">*</span> <span className="text-[#868E96] normal-case tracking-normal font-normal">— puis vos métiers secondaires (facultatif)</span></Label>
              <div className="mt-2 border border-[#DEE2E6] p-3 bg-[#F8F9FA]">
                <MetierPicker value={metier} onChange={(code, m) => { setMetier(code); setMetierObj(m); }} secondaires={secondaires} onSecondairesChange={setSecondaires} testid="wizard-metier" />
              </div>
              <div className="text-[10px] text-[#868E96] mt-2" data-testid="wizard-metier-help">
                {metierObj ? `${metierObj.emoji || ""} ${metierObj.label} — ${metierObj.audience}. ` : ""}Ce choix débloque les modules métier adaptés (HACCP restauration, rondes sécurité, réservations hébergement…). Le métier principal est ensuite modifiable par PAG, les secondaires par vous.
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={submitStep1} disabled={submitting || !secteur} data-testid="wizard-step1-next" className="h-11 bg-[#002FA7] text-white">
                {submitting ? "…" : "Continuer"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Logo */}
        {step === 2 && (
          <div className="space-y-4 py-2" data-testid="wizard-step-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Logo de votre entreprise (optionnel)</Label>
            <div className="grid grid-cols-2 gap-4">
              <label className="border-2 border-dashed border-[#DEE2E6] hover:border-[#002FA7] transition-colors p-6 text-center cursor-pointer flex flex-col items-center justify-center min-h-[140px]" data-testid="wizard-logo-pick">
                <ImageIcon className="h-8 w-8 text-[#002FA7] mb-2" />
                <span className="text-xs font-semibold">Cliquez pour choisir</span>
                <span className="text-[10px] text-[#868E96] mt-1">PNG, JPG, WEBP · Max 5 Mo</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => onPickLogo(e.target.files?.[0])} className="hidden" data-testid="wizard-logo-input" />
              </label>
              <div className="border border-[#DEE2E6] p-3 flex items-center justify-center bg-[#F8F9FA] min-h-[140px]">
                {logoPreview ? (
                  <img src={logoPreview} alt="Aperçu" className="max-h-full max-w-full object-contain" data-testid="wizard-logo-preview" />
                ) : (
                  <div className="text-xs text-[#868E96] text-center">Aperçu du logo</div>
                )}
              </div>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => submitStep2(true)} data-testid="wizard-step2-skip" className="h-11">
                <SkipForward className="mr-2 h-4 w-4" /> Passer cette étape
              </Button>
              <Button onClick={() => submitStep2(false)} disabled={submitting || !logoFile} data-testid="wizard-step2-next" className="h-11 bg-[#002FA7] text-white">
                {submitting ? "…" : "Enregistrer"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Salarié */}
        {step === 3 && (
          <div className="space-y-3 py-2" data-testid="wizard-step-3">
            <div className="text-[10px] text-[#868E96] uppercase tracking-wider">Premier salarié — vous en aurez sûrement besoin</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom *" value={sal.nom} onChange={(v) => setSal({ ...sal, nom: v })} testid="wiz-sal-nom" />
              <Field label="Prénom *" value={sal.prenom} onChange={(v) => setSal({ ...sal, prenom: v })} testid="wiz-sal-prenom" />
              <Field label="Email" type="email" value={sal.email} onChange={(v) => setSal({ ...sal, email: v })} testid="wiz-sal-email" />
              <Field label="Téléphone *" type="tel" value={sal.telephone} onChange={(v) => setSal({ ...sal, telephone: v })} testid="wiz-sal-tel" />
              <Field label="Poste" value={sal.poste} onChange={(v) => setSal({ ...sal, poste: v })} testid="wiz-sal-poste" />
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Contrat</Label>
                <Select value={sal.contrat} onValueChange={(v) => setSal({ ...sal, contrat: v })}>
                  <SelectTrigger className="h-10 mt-1" data-testid="wiz-sal-contrat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Intérim">Intérim</SelectItem>
                    <SelectItem value="Alternance">Alternance</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Taux horaire (€)" type="number" value={sal.taux_horaire} onChange={(v) => setSal({ ...sal, taux_horaire: v })} testid="wiz-sal-taux" />
              <Field label="Mot de passe (≥6) *" type="password" value={sal.password} onChange={(v) => setSal({ ...sal, password: v })} testid="wiz-sal-pass" />
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => submitStep3(true)} data-testid="wizard-step3-skip" className="h-11" disabled={submitting}>
                <SkipForward className="mr-2 h-4 w-4" /> Plus tard
              </Button>
              <Button onClick={() => submitStep3(false)} disabled={submitting} data-testid="wizard-step3-finish" className="h-11 bg-[#059669] text-white">
                <CheckCircle2 className="mr-2 h-4 w-4" /> {submitting ? "…" : "Créer & terminer"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 mt-1" data-testid={testid} />
    </div>
  );
}
